import { Modal, ModalProps } from '@/modules/common/components/Modal';
import { MoneyInput } from '@/modules/common/components/MoneyInput';
import {
  integerToMoney,
  moneyToInteger,
} from '@/modules/common/lib/moneyHandler';
import { useGetTeam } from '@/modules/employees/hooks/useTeams';
import {
  IAmount,
  IEmployeeOverrideWithEmployee,
} from '@/types/learning-budget';
import { yupResolver } from '@hookform/resolvers/yup';
import { InfoOutlined } from '@mui/icons-material';
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

type EditEmployeeLearningBudgetModalProps = Omit<
  ModalProps,
  'onClose' | 'title' | 'subTitle'
> & {
  onClose: (data?: IEmployeeOverrideWithEmployee) => void;
  employeeOverride: IEmployeeOverrideWithEmployee | undefined;
};

const schema = yup.object().shape({
  budget: yup.object().shape({
    amount: yup
      .number()
      .typeError('Amount is required')
      .required('Amount is required')
      .positive('Amount must be positive'),
    currency: yup.string().required().oneOf(['EUR']),
  }),
});

export function EditEmployeeLearningBudgetModal({
  open,
  onClose,
  employeeOverride,
  ...props
}: EditEmployeeLearningBudgetModalProps) {
  const { data: teamData, isLoading: isLoadingTeam } = useGetTeam(
    employeeOverride?.employee.team ?? '',
    {
      enabled: !!employeeOverride?.employee.team,
    },
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<{ budget: IAmount }>({
    values: {
      budget: employeeOverride?.budget ?? {
        amount: employeeOverride?.budget?.amount ?? 0,
        currency: employeeOverride?.budget?.currency ?? 'EUR',
      },
    },
    resolver: yupResolver(schema),
  });

  const handleCancel = () => {
    onClose();
    reset();
  };

  const handleSave = (data: { budget: IAmount }) => {
    if (!employeeOverride) {
      return;
    }

    onClose({
      ...employeeOverride,
      budget: {
        amount: data.budget.amount,
        currency: data.budget.currency,
      },
    });
    reset();
  };

  const employeeName = [
    employeeOverride?.employee.firstName,
    employeeOverride?.employee.lastName,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <Modal
      {...props}
      open={open}
      onClose={handleCancel}
      title={`Edit Learning Budget for ${employeeName}`}
      subTitle="Modify Budget Allocation for this Member"
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleCancel}
            disabled={isSubmitting || isLoadingTeam}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="edit-learning-budget-form"
            disabled={isLoadingTeam}
            loading={isSubmitting}
          >
            Save
          </Button>
        </>
      }
      classes={{ paper: 'max-w-md' }}
    >
      <form id="edit-learning-budget-form" onSubmit={handleSubmit(handleSave)}>
        <Stack gap={3}>
          <div>
            <Typography variant="subtitle2" fontWeight={600}>
              Employee Team
            </Typography>
            {isLoadingTeam ? (
              <Skeleton variant="text" width={100} height={24} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {teamData?.data?.name || 'No team selected'}
              </Typography>
            )}
          </div>

          <FormControl className="flex flex-col gap-1">
            <Typography
              variant="subtitle2"
              component="label"
              htmlFor="budget-amount"
            >
              Learning Budget
            </Typography>
            <Controller
              name="budget.amount"
              control={control}
              render={({
                field: { value, onChange, ref: inputRef, ...field },
              }) => (
                <MoneyInput
                  {...field}
                  value={integerToMoney(value)}
                  onChange={e => {
                    onChange(moneyToInteger(e.target.value));
                  }}
                  inputRef={inputRef}
                  id="learning-budget-amount"
                  decimalScale={2}
                  startAdornment={
                    <Typography
                      className={`text-2xl font-semibold ${
                        isSubmitting ? 'text-gray-400' : ''
                      }`}
                    >
                      €
                    </Typography>
                  }
                  allowNegative={true}
                  classes={{
                    root: 'flex-1 rounded-none text-center',
                    input: 'text-center text-2xl font-semibold',
                    focused: 'outline-none',
                  }}
                />
              )}
            />
            {errors.budget?.amount && (
              <FormHelperText error>
                {errors.budget.amount.message}
              </FormHelperText>
            )}
          </FormControl>

          <Alert
            severity="info"
            variant="filled"
            className="bg-info-light text-info-dark rounded-lg"
            icon={<InfoOutlined />}
          >
            To assign a team to this member, open the Employees page, select the
            Teams tab, or click the 'Manage Teams' button.
          </Alert>
        </Stack>
      </form>
    </Modal>
  );
}
